import { Group } from "@iventis/types";
import { includeClustersInGroups } from "@iventis/utilities";
import { RowGridLayout, RowGridLayoutCluster, RowGridLayoutNestedChild, RowGridLayoutNested } from "../interactive-grid.types";
import { clusterHeight } from "./constants";

export const findGroup = (groups: Group[], id: string): Group =>
    groups.reduce((foundGroup, group) => foundGroup || (group.groupId === id ? group : findGroup(group.subGroups || [], id)), undefined as Group);

/**
 * Gets the inset box shadow rule for a row border.
 * Inset box is used because it does not affect layout unlike border
 * @param show Whether to show the border
 * @returns The box shadow CSS rule
 */
export const getRowBorder = (colour: string) => `0 -1px 0 ${colour}`;

export const isTouchEvent = (e: MouseEvent | TouchEvent): e is TouchEvent => (e as TouchEvent).touches !== undefined;

export const extractClientXClientY = (e: MouseEvent | TouchEvent) =>
    isTouchEvent(e) ? { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY } : { clientX: e.clientX, clientY: e.clientY };

export function columnGridLayoutFromGroups(groups: Group[], groupColumnWidth: number): { groupId: string; columnNumber: number; groupColumnWidth: string }[] {
    return groups.map((group, index) => ({ groupId: group.groupId, columnNumber: index + 1, groupColumnWidth: `${groupColumnWidth}px` }));
}

export function rowGridLayoutFromGroups(groups: Group[]): RowGridLayout {
    const spans: RowGridLayout = [];

    // Grid rows start at 1, declare explicit counter since we're navigating a tree structure but need to output it in a single-dimensional array.
    let counter = 1;

    const createStandardRow = (groupId: string, subGroups: Group[]): RowGridLayoutNested => {
        const children: RowGridLayoutNestedChild[] = subGroups.map(({ groupId: thirdLevelGroupId }) => {
            const nestedChild: RowGridLayoutNestedChild = { groupId: thirdLevelGroupId, rowNumber: counter, height: "auto" };
            counter += 1;
            return nestedChild;
        });
        return { groupId, children, wideCluster: false, groupRowHeight: "auto" } as RowGridLayoutNested;
    };

    groups.forEach(({ groupId, subGroups }) => {
        if (includeClustersInGroups(groups)) {
            spans.push({ groupId, rowNumber: counter, wideCluster: true, groupRowHeight: clusterHeight } as RowGridLayoutCluster);
            counter += 1;
            subGroups.forEach(({ groupId: secondLevelGroupId, subGroups }) => {
                spans.push(createStandardRow(secondLevelGroupId, subGroups));
            });
        } else {
            spans.push(createStandardRow(groupId, subGroups));
        }
    });

    return spans;
}

export function isWideRow(row: RowGridLayoutCluster | RowGridLayoutNested): row is RowGridLayoutCluster {
    return row.wideCluster;
}

export function gridTemplateRowsFromRowGridLayout(rowGridLayout: RowGridLayout) {
    let gridTemplateRows = "";

    rowGridLayout.forEach((rowLayout) => {
        if (isWideRow(rowLayout)) {
            gridTemplateRows += ` ${rowLayout.groupRowHeight}`;
        } else {
            rowLayout.children.forEach((child) => {
                gridTemplateRows += ` ${child.height}`;
            });
        }
    });

    return gridTemplateRows;
}
